/**
 * RecordPageHeader – Shared header for record-type pages (Appointment, Discharge Summary, Treatment Plan).
 * Header block background is always gold; only the record type icon and label use recordTypeColor (blue/green/purple).
 */
import type { ReactNode } from 'react';
import { Box, Group, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCalendar, IconMail, IconPhone, IconUser } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

export type RecordTypeColor = 'blue' | 'green' | 'purple';

const LABEL_COLOR: Record<RecordTypeColor, string> = {
  blue: 'var(--mantine-color-blue-7)',
  green: 'var(--mantine-color-green-7)',
  purple: 'var(--mantine-color-violet-7)',
};

export interface RecordPageHeaderProps {
  /** Record type label (e.g. "Appointment", "Discharge Summary", "Treatment Plan") */
  recordType: string;
  /** Color for icon and label only: blue (appointments), green (discharge), purple (treatment plan) */
  recordTypeColor: RecordTypeColor;
  /** Icon element; use color matching recordTypeColor (e.g. blue-7 for appointments) */
  icon: ReactNode;
  /** Primary large title (e.g. date; use gray when not a link) */
  primaryTitle?: ReactNode;
  /** Optional secondary large title (e.g. episode name; use gold only when it's a link) */
  secondaryTitle?: ReactNode;
  /** Optional row below titles (e.g. time + provider); keep text non-gold and sized as needed */
  secondaryRow?: ReactNode;
  /** Optional content for top-right of header (e.g. Signed badge, status badge) */
  rightSide?: ReactNode;
  /** Link to client chart / medical record; if empty, name is not a link */
  clientHref?: string;
  /** Preferred / display name */
  clientPreferredName: string;
  /** Legal name when different from preferred (shown in parentheses) */
  clientLegalName?: string;
  /** Pronouns (e.g. "she/her") */
  clientPronouns?: string;
  /** Formatted DOB for display (optional) */
  clientDob?: string;
  /** Client phone (optional) */
  clientPhone?: string;
  /** Client email (optional) */
  clientEmail?: string;
  /** Optional extra content at end of client row (e.g. "Episode: X") */
  clientExtra?: ReactNode;
  /** Optional metadata row below client row (e.g. session attendance) */
  clientMetadata?: ReactNode;
  /** Optional section below client row (e.g. previous/next appointment links) */
  bottomSection?: ReactNode;
}

export function RecordPageHeader({
  recordType,
  recordTypeColor,
  icon,
  primaryTitle,
  secondaryTitle,
  secondaryRow,
  rightSide,
  clientHref,
  clientPreferredName,
  clientLegalName,
  clientPronouns,
  clientDob,
  clientPhone,
  clientEmail,
  clientExtra,
  clientMetadata,
  bottomSection,
}: RecordPageHeaderProps): JSX.Element {
  const labelColor = LABEL_COLOR[recordTypeColor];
  const themeIconColor = recordTypeColor === 'purple' ? 'violet' : recordTypeColor;
  return (
    <Paper withBorder style={{ overflow: 'hidden' }}>
      {/* ── Section 1: Record identity (gold block; icon + label use recordTypeColor) ── */}
      <Box
        px="lg"
        pt="md"
        pb="sm"
        style={{
          backgroundColor: 'var(--mantine-color-gold-1)',
          borderBottom: '1px solid var(--mantine-color-gold-2)',
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={6}>
            <Group gap={6} align="center">
              <ThemeIcon size="sm" variant="light" color={themeIconColor} radius="sm">
                {icon}
              </ThemeIcon>
              <Text size="xs" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em', color: labelColor }}>
                {recordType}
              </Text>
            </Group>
            {secondaryTitle != null && (
              <Title order={2} style={{ lineHeight: 1.15, fontWeight: 700 }}>
                {secondaryTitle}
              </Title>
            )}
            {primaryTitle != null && (
              <Title order={2} style={{ lineHeight: 1.15, fontWeight: 700 }}>
                {primaryTitle}
              </Title>
            )}
            {secondaryRow != null && (
              <Group gap="sm" wrap="wrap" align="center">
                {secondaryRow}
              </Group>
            )}
          </Stack>
          {rightSide != null && (
            <Stack gap="sm" align="flex-end" style={{ flexShrink: 0 }}>
              {rightSide}
            </Stack>
          )}
        </Group>
      </Box>

      {/* ── Section 2: Client context — Client, [Preferred Name (Legal Name)], Pronouns, DOB, Phone, Email ── */}
      <Box px="lg" py="sm">
        <Group gap="md" align="center" wrap="wrap">
          <Group gap={6} align="center" style={{ flexShrink: 0 }}>
            <ThemeIcon size="xs" variant="subtle" color="gray" radius="xl">
              <IconUser size={11} />
            </ThemeIcon>
            <Text size="xs" tt="uppercase" fw={700} c="gray.5" style={{ letterSpacing: '0.06em' }}>
              Client
            </Text>
          </Group>
          <Group gap={4} align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
            {clientHref ? (
              <Text component={Link} to={clientHref} fw={600} size="sm" c="gold.7" style={{ lineHeight: 1.3, textDecoration: 'none' }}>
                {clientPreferredName}
              </Text>
            ) : (
              <Text fw={600} size="sm" c="gray.8" style={{ lineHeight: 1.3 }}>{clientPreferredName}</Text>
            )}
            {clientLegalName != null && clientLegalName !== '' && clientLegalName !== clientPreferredName && (
              <Text size="sm" c="gray.5" style={{ lineHeight: 1.3 }}>({clientLegalName})</Text>
            )}
          </Group>
          {clientPronouns != null && clientPronouns !== '' && (
            <>
              <Text size="sm" c="gray.3">|</Text>
              <Text size="sm" c="gray.6">{clientPronouns}</Text>
            </>
          )}
          {clientDob && (
            <>
              <Text size="sm" c="gray.3">|</Text>
              <Group gap={4}>
                <IconCalendar size={13} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="gray.6">{clientDob}</Text>
              </Group>
            </>
          )}
          {clientPhone && (
            <>
              <Text size="sm" c="gray.3">|</Text>
              <Group gap={4}>
                <IconPhone size={13} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="gray.6">{clientPhone}</Text>
              </Group>
            </>
          )}
          {clientEmail && (
            <>
              <Text size="sm" c="gray.3">|</Text>
              <Group gap={4}>
                <IconMail size={13} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="gray.6">{clientEmail}</Text>
              </Group>
            </>
          )}
          {clientExtra}
        </Group>
      </Box>

      {clientMetadata != null && (
        <Box px="lg" pb="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }} pt="sm">
          {clientMetadata}
        </Box>
      )}
      {bottomSection != null && <Box px="lg" pb="lg">{bottomSection}</Box>}
    </Paper>
  );
}
